import React, { useState, useCallback, useEffect, useRef } from 'react';
// import { BrowserRouter, Link, Route, Switch } from 'react-router-dom' ;
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import Header from './components/layouts/header';
import Landing from './components/layouts/landing';
import Footer from './components/layouts/footer';
import NewGame from './pages/NewGame/newGame';
import Favorite from './pages/favorites/favorite';
import Slot from './pages/slot/slot';
import Live from './pages/live/live';
import GameDemo from './pages/live/components/gameDemo';
import Login from './components/auth/login';
import Notification from './components/common/notification';
import echo from './lib/echo';
import { useBalancePolling } from './hooks/useBalancePolling';
import ToastListener from "./components/common/ToastListener";
import {logout} from './services/authService'
import axios from 'axios';
import './App.css';
import GamePage from './pages/games/GamePage';
import { getTheme } from './themes/config';

function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [appKey, setAppKey] = useState(0);
  const isLoggingOutRef = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if user is authenticated from localStorage (check for token)
    const token = localStorage.getItem('authToken');
    return token !== null && localStorage.getItem('isAuthenticated') === 'true';
  });
  const [userData, setUserData] = useState(() => {
    // Get user data from localStorage on mount
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        return JSON.parse(storedUserData);
      } catch (error) {
        return null;
      }
    }
    return null;
  });
  const [userBalance, setUserBalance] = useState(() => {
    // Get user balance from localStorage on mount
    const storedBalance = localStorage.getItem('userBalance');
    if (storedBalance) {
      try {
        return JSON.parse(storedBalance);
      } catch (error) {
        return null;
      }
    }
    return null;
  });
  const [notification, setNotification] = useState({ message: '', isVisible: false });
  const userDataRef = useRef(userData);
  const userBalanceRef = useRef(userBalance);

  // Memoize the balance update callback to prevent unnecessary re-renders
  const handleBalanceUpdate = useCallback((balanceData) => {
    setUserBalance(balanceData);
  }, []);

  useEffect(() => {
    const theme = getTheme();

    // apply CSS variables
    Object.entries(theme.cssVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    // favicon
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) favicon.href = theme.favicon;

    // title
    document.title = theme.name;
  }, []);

  // Poll balance every 1 second when authenticated
  useBalancePolling(isAuthenticated, handleBalanceUpdate);
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error?.response?.status === 401 && !isLoggingOutRef.current) {
          console.warn('[Axios Global] 401 detected → force logout');

          isLoggingOutRef.current = true;

          window.dispatchEvent(new Event('forceLogout'));

          // reset guard sau 1s
          setTimeout(() => {
            isLoggingOutRef.current = false;
          }, 1000);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);
  useEffect(() => {
    const onForceLogout = () => {
      const token = localStorage.getItem("authToken")
      if(token){
        try {
          echo.disconnect();
        } catch (e) {}

        handleSignOut();
      }
     
    };

    window.addEventListener('forceLogout', onForceLogout);

    return () => {
      window.removeEventListener('forceLogout', onForceLogout);
    };
  }, []);
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    userBalanceRef.current = userBalance;
  }, [userBalance]);

  useEffect(() => {
    const currentUser = userDataRef.current || userData;
    const userId = currentUser?.id;

    if (!isAuthenticated || !userId) {
      return undefined;
    }

    const channelName = `user.${userId}`;
    const channel = echo.private(channelName);

    const handleBalanceUpdated = (event) => {
    // "Deposit completed" -> reload iframe
    if (event?.message && event.message.includes("is reloaded after deposit")) {

        window.dispatchEvent(new CustomEvent("DepositCompleted"));
    }
    // "Withdrawal completed" -> reload iframe
    if (event?.message && event.message.includes("is reloaded after withdraw")) {
        window.dispatchEvent(new CustomEvent("withdrawalCompleted"));
    }

    if (event?.message && event.message.includes("Bet limit exceeded")) {
        window.dispatchEvent(new CustomEvent("BetLimitExceeded"));
    }

    if (event?.message && event.message.includes("Change clone game for user")) {
      const external = event?.data?.user?.external || {};
      window.dispatchEvent(
        new CustomEvent("UseGameClone", {
          detail: {
            game_code: external.game_code,
            game_code_clone: external.game_code_clone,
          }
        })
      );
    }
      const previousBalance = userBalanceRef.current;
      const currentUserSnapshot = userDataRef.current;

      let balanceData = null;

      const normalizeWithUser = (payloadUser, fallbackBalance) => {
        if (!payloadUser || typeof payloadUser !== 'object') {
          return null;
        }

        const normalizedBalance = Number(
          typeof payloadUser.balance !== 'undefined'
            ? payloadUser.balance
            : fallbackBalance
        );

        const baseBalance = (previousBalance && typeof previousBalance === 'object') ? previousBalance : {};

        return {
          ...baseBalance,
          balance: isNaN(normalizedBalance) ? baseBalance.balance : normalizedBalance,
          realBalance: isNaN(normalizedBalance) ? baseBalance.realBalance : normalizedBalance,
          user: {
            ...(baseBalance.user && typeof baseBalance.user === 'object' ? baseBalance.user : {}),
            ...payloadUser,
            balance: isNaN(normalizedBalance) ? payloadUser.balance : normalizedBalance,
          },
        };
      };

      if (event?.balanceData) {
        balanceData = event.balanceData;
      } else if (event?.data?.balanceData) {
        balanceData = event.data.balanceData;
      } else if (event?.data) {
        balanceData = event.data;
      } else if (
        event?.status &&
        event?.data &&
        event.data.user
      ) {
        balanceData = normalizeWithUser(event.data.user, previousBalance?.realBalance ?? previousBalance?.balance);
      } else if (typeof event?.newBalance !== 'undefined') {
        const normalizedValue = Number(event.newBalance);
        const baseBalance = (previousBalance && typeof previousBalance === 'object') ? previousBalance : {};
        const baseUser = (baseBalance.user && typeof baseBalance.user === 'object')
          ? baseBalance.user
          : (currentUserSnapshot && typeof currentUserSnapshot === 'object' ? { ...currentUserSnapshot } : {});

        balanceData = {
          ...baseBalance,
          balance: normalizedValue,
          realBalance: normalizedValue,
          user: {
            ...baseUser,
            balance: normalizedValue,
          },
        };
      } else if (
        typeof event?.balance !== 'undefined' ||
        typeof event?.realBalance !== 'undefined' ||
        typeof event?.bonusBalance !== 'undefined' ||
        typeof event?.point !== 'undefined' ||
        (event && typeof event === 'object' && event.user)
      ) {
        balanceData = event;
      }

      if (!balanceData && event?.data?.user) {
        balanceData = normalizeWithUser(event.data.user, previousBalance?.realBalance ?? previousBalance?.balance);
      }

      if (!balanceData) {
        console.warn('[Echo] Balance update received without recognizable payload', event);
        return;
      }


      const updatedUser = balanceData.user || event?.data?.user;

      if (updatedUser) {
        try {
          localStorage.setItem('userData', JSON.stringify({
            ...(currentUserSnapshot && typeof currentUserSnapshot === 'object' ? currentUserSnapshot : {}),
            ...updatedUser,
          }));
          setUserData((prev) => ({
            ...(prev && typeof prev === 'object' ? prev : {}),
            ...updatedUser,
          }));
        } catch (userStorageError) {
          console.error('Unable to persist updated user data to localStorage', userStorageError);
        }
      }

      try {
        localStorage.setItem('userBalance', JSON.stringify(balanceData));
      } catch (storageError) {
        console.error('Unable to persist updated balance to localStorage', storageError);
      }

      try {
        window.dispatchEvent(new CustomEvent('balanceUpdated', {
          detail: { balanceData },
        }));
      } catch (dispatchError) {
        console.error('Unable to dispatch balanceUpdated event', dispatchError);
      }

      setUserBalance(balanceData);
    };

    channel.listen('.balance.updated', handleBalanceUpdated);

    return () => {
      channel.stopListening('.balance.updated');
      echo.leave(channelName);
    };
  }, [isAuthenticated, userData?.id]);

  const handleLoginSuccess = (userData, token, balanceData) => {
    setIsAuthenticated(true);
    setUserData(userData);
    setUserBalance(balanceData);
    setShowLogin(false);
    // User data, token, and balance are already saved in localStorage by login component
    setAppKey(prev => prev + 1);
    // Show welcome notification
    const username = userData?.username || userData?.email || userData?.name || 'User';
    setNotification({
      message: `Welcome, ${username}`,
      isVisible: true
    });
  };

  const handleSignOut = async () => {
    // Clear localStorage
    await logout();
    setAppKey(prev => prev + 1);
    // Update state
    setIsAuthenticated(false);
    setUserData(null);
    setUserBalance(null);
    
  };

  return (
    <Router>
         <Routes>

        {/* Layout a Header + Footer */}
        <Route element={
          <div className="App">
            <Header
              showLogin={() => setShowLogin(true)}
              isAuthenticated={isAuthenticated}
              userData={userData}
              userBalance={userBalance}
              onSignOut={handleSignOut}
              onLoginSuccess={handleLoginSuccess}
            />

            <Outlet />

            <Footer />
          </div>
        }>

          <Route path="/" element={<Landing showLogin={() => setShowLogin(true)} isAuthenticated={isAuthenticated} userData={userData} userBalance={userBalance} onSignOut={handleSignOut} />} />

          <Route path="/newgame" element={<NewGame showLogin={() => setShowLogin(true)} isAuthenticated={isAuthenticated} userData={userData} userBalance={userBalance} onSignOut={handleSignOut} />} />

          <Route path="/favorite" element={<Favorite showLogin={() => setShowLogin(true)} isAuthenticated={isAuthenticated} userData={userData} userBalance={userBalance} onSignOut={handleSignOut} />} />

          <Route path="/slot" element={<Slot showLogin={() => setShowLogin(true)} isAuthenticated={isAuthenticated} userData={userData} userBalance={userBalance} onSignOut={handleSignOut} />} />

          <Route path="/live" element={<Live showLogin={() => setShowLogin(true)} isAuthenticated={isAuthenticated} userData={userData} userBalance={userBalance} onSignOut={handleSignOut} />} />

        </Route>

        {/* Game page has NO header*/}
        <Route
          path="/game"
          element={
              <GamePage
                showLogin={() => setShowLogin(true)}
                isAuthenticated={isAuthenticated}
              />
          }
        />
           <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>

      <Login
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onLoginSuccess={handleLoginSuccess}
      />
      <Notification
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={() => setNotification({ message: "", isVisible: false })}
      />
      <ToastListener />
    </Router>
  );
}

export default App;
